import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ShieldCheck, Heart, Award, Users, ArrowRight } from "lucide-react";
import { companyInfo } from "../data";

export default function About() {
  const aboutImageResized = "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00030.jpeg";

  return (
    <>
      <Helmet>
        <title>Om Os - Henneberg Byggeri ApS</title>
        <meta
          name="description"
          content="Læs historien bag Henneberg Byggeri ApS. Vi er en lokal tømrervirksomhed i Janderup med fokus på høj faglighed, personlig rådgivning og solidt håndværk."
        />
        <meta property="og:title" content="Om Os - Henneberg Byggeri ApS" />
        <meta
          property="og:description"
          content="Læs historien bag Henneberg Byggeri ApS. Vi er en lokal tømrervirksomhed i Janderup med fokus på høj faglighed."
        />
        <link rel="canonical" href="https://hennebergbyggeri.dk/om-os" />
      </Helmet>

      {/* Virksomhedspræsentation & Historie */}
      <section className="py-16 sm:py-24 bg-white text-neutral-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-center">
            <div className="space-y-6">
              <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl uppercase">
                Om Henneberg Byggeri ApS
              </h1>
              <div className="space-y-4 text-base text-gray-600 leading-relaxed">
                <p>
                  Henneberg Byggeri ApS er en tømrervirksomhed i Janderup med fokus på høj faglighed, personlig rådgivning og solidt håndværk. Vi dækker alt fra mindre reparationsopgaver til store totalentrepriser, hvor kunden altid er i centrum.
                </p>
              </div>
              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-none bg-[#434359] px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-[#343446] active:scale-95 shadow-md"
                >
                  KONTAKT OS I DAG
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00018.jpeg"
                alt="Moderne byggeri under opførelse af Henneberg Byggeri"
                className="shadow-xl object-cover w-full h-[450px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Værdier Sektion */}
      <section className="py-16 sm:py-24 bg-neutral-50 text-neutral-900 border-t border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl uppercase">
              De værdier vi bygger på
            </h2>
            <p className="mt-4 text-base text-gray-600">
              For os handler byggeri om relationer. Vi ønsker, at du skal føle dig tryg og velinformeret gennem hele byggeprocessen.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-neutral-200">
            {/* Værdi 1 */}
            <div className="p-6 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center text-neutral-900 mb-4">
                <ShieldCheck className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900 uppercase">Høj Kvalitet</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                Vi anvender udelukkende godkendte kvalitetsmaterialer og udfører arbejdet efter alle gældende normer og regler.
              </p>
            </div>

            {/* Værdi 2 */}
            <div className="p-6 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center text-neutral-900 mb-4">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900 uppercase">Faglig Stolthed</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                Vores tømrere elsker deres fag. Vi kæler for detaljerne og afleverer aldrig et projekt, før det er 100% i orden.
              </p>
            </div>

            {/* Værdi 3 */}
            <div className="p-6 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center text-neutral-900 mb-4">
                <Heart className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900 uppercase">Ærlig Rådgivning</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                Vi rådgiver dig ærligt om materialer, løsninger og økonomi, så du får mest muligt ud af dit byggebudget.
              </p>
            </div>

            {/* Værdi 4 */}
            <div className="p-6 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center text-neutral-900 mb-4">
                <Users className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900 uppercase">Tæt Samarbejde</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                Du har én fast kontaktperson gennem hele forløbet, hvilket sikrer hurtig kommunikation og færre misforståelser.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lokalt Engagement */}
      <section className="py-16 sm:py-24 bg-white text-neutral-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-center">
            <div className="relative order-last lg:order-first">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00013.jpeg"
                alt="Henneberg Byggeri lokalt engagement i Janderup"
                className="shadow-xl object-cover w-full h-[400px]"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl uppercase">
                Forankret i Janderup og det vestjyske
              </h2>
              <div className="space-y-4 text-base text-gray-600 leading-relaxed">
                <p>
                  Som lokal virksomhed med base på Vr Janderupvej 11 i Janderup Vestj, føler vi et stort ansvar for vores lokalområde. Vi tror på, at et stærkt lokalsamfund skabes gennem samarbejde, opbakning og gensidig støtte.
                </p>
                <p>
                  Når du vælger Henneberg Byggeri, vælger du en lokal håndværker, der kender områdets jordbundsforhold, arkitektur og traditioner. Vi er altid lige i nærheden, hvis der opstår akutte behov, eller hvis du blot ønsker en uformel snak om et kommende projekt.
                </p>
                <p>
                  Vi støtter lokale initiativer og foreninger, og vi bestræber os altid på at benytte lokale underentreprenører og leverandører, når det er muligt. Det gavner både miljøet i form af kortere transporttider og styrker det lokale erhvervsliv.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Sektion */}
      <section className="py-16 sm:py-20 bg-neutral-50 text-neutral-900 border-t border-gray-100">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl uppercase text-neutral-900">
            Lad os bygge dit næste projekt sammen
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Uanset om du skal have renoveret facaden, lagt nyt tag eller bygget en helt ny træterrasse, står Henneberg Byggeri klar med professionel ekspertise.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Link
              to="/kontakt"
              className="rounded-none bg-[#434359] px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-[#343446] hover:shadow-lg active:scale-95"
            >
              Kontakt os i dag
            </Link>
            <Link
              to="/galleri"
              className="rounded-none border border-black bg-transparent px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-black transition-all duration-300 hover:bg-black/5"
            >
              Se vores referencer
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
